/**
 * TestPlanDetailPanel
 * Renders structured test plan details in the right panel
 * when the agent returns test plan data from list_test_plans or get_test_plan tools.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Grid,
  LinearProgress,
  IconButton,
  Button,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

// ============================================================================
// Types
// ============================================================================

export interface TestPlanDetailPanelProps {
  plans: any[];
  selectedPlan: any | null;
  onSelectPlan: (plan: any) => void;
  onClose: () => void;
  onAskAgent: (prompt: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'draft':
      return 'default';
    case 'active':
    case 'in_progress':
      return 'primary';
    case 'completed':
      return 'success';
    case 'archived':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (priority) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
    default:
      return 'default';
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

// ============================================================================
// Component
// ============================================================================

const TestPlanDetailPanel: React.FC<TestPlanDetailPanelProps> = ({
  plans,
  selectedPlan,
  onSelectPlan,
  onClose,
  onAskAgent,
}) => {
  const [objectivesExpanded, setObjectivesExpanded] = useState(false);

  const plan = selectedPlan;

  // Compute execution progress
  const totalCases = plan?.total_test_cases || plan?.execution_summary?.total || 0;
  const passedCases = plan?.execution_summary?.passed || 0;
  const failedCases = plan?.execution_summary?.failed || 0;
  const skippedCases = plan?.execution_summary?.skipped || 0;
  const executedCases = passedCases + failedCases + skippedCases;
  const progressPercent = totalCases > 0 ? Math.round((executedCases / totalCases) * 100) : 0;

  const tmsBaseUrl = window.location.origin.replace(':3003', ':3000');

  const handleViewInTMS = () => {
    if (plan?.project_id && plan?.id) {
      window.open(`${tmsBaseUrl}/projects/${plan.project_id}/test-plans/${plan.id}`, '_blank');
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Test Plans
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close panel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
        {/* Plan Selector (when multiple plans) */}
        {plans.length > 1 && (
          <>
            <List dense disablePadding sx={{ mb: 1.5, maxHeight: 180, overflow: 'auto' }}>
              {plans.map((p) => (
                <ListItemButton
                  key={p.id}
                  selected={plan?.id === p.id}
                  onClick={() => onSelectPlan(p)}
                  sx={{ borderRadius: 1, py: 0.5 }}
                >
                  <ListItemText
                    primary={p.name}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                    <Chip
                      size="small"
                      label={p.status || 'draft'}
                      color={getStatusColor(p.status)}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  </Box>
                </ListItemButton>
              ))}
            </List>
            <Divider sx={{ mb: 1.5 }} />
          </>
        )}

        {/* Detail View */}
        {plan && (
          <Box>
            {/* Name + Status + Priority */}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              {plan.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip
                size="small"
                label={plan.status || 'draft'}
                color={getStatusColor(plan.status)}
              />
              {plan.priority && (
                <Chip
                  size="small"
                  label={plan.priority}
                  color={getPriorityColor(plan.priority)}
                  variant="outlined"
                />
              )}
              {plan.test_plan_type && (
                <Chip
                  size="small"
                  label={plan.test_plan_type}
                  variant="outlined"
                />
              )}
            </Box>

            {/* Info Section */}
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              {plan.version && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Version</Typography>
                  <Typography variant="body2">{plan.version}</Typography>
                </Grid>
              )}
              {plan.project_version && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Project Version</Typography>
                  <Typography variant="body2">{plan.project_version}</Typography>
                </Grid>
              )}
              {plan.created_at && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatDate(plan.created_at)}</Typography>
                </Grid>
              )}
              {plan.project_id && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Project ID</Typography>
                  <Typography variant="body2">{plan.project_id}</Typography>
                </Grid>
              )}
            </Grid>

            {/* Description */}
            {plan.description && (
              <Box sx={{ mb: 1.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setObjectivesExpanded(!objectivesExpanded)}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Description
                  </Typography>
                  {objectivesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
                <Collapse in={objectivesExpanded}>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {plan.description}
                  </Typography>
                </Collapse>
                {!objectivesExpanded && (
                  <Typography variant="body2" noWrap sx={{ opacity: 0.7 }}>
                    {plan.description}
                  </Typography>
                )}
              </Box>
            )}

            {/* Execution Progress */}
            {totalCases > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Execution Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption">{progressPercent}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="success.main">
                    {passedCases} passed
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    {failedCases} failed
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {skippedCases} skipped
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {totalCases} total
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Schedule */}
            {(plan.start_date || plan.end_date) && (
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start</Typography>
                  <Typography variant="body2">{formatDate(plan.start_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">End</Typography>
                  <Typography variant="body2">{formatDate(plan.end_date)}</Typography>
                </Grid>
              </Grid>
            )}

            {/* Metrics */}
            {(plan.target_pass_rate || plan.coverage_target) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Targets</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  {plan.target_pass_rate && (
                    <Chip size="small" label={`Pass: ${plan.target_pass_rate}%`} variant="outlined" />
                  )}
                  {plan.coverage_target && (
                    <Chip size="small" label={`Coverage: ${plan.coverage_target}%`} variant="outlined" />
                  )}
                </Box>
              </Box>
            )}

            {/* AI Insights */}
            {(plan.risk_score != null || plan.ai_recommendations) && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">AI Insights</Typography>
                {plan.risk_score != null && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Risk Score: <strong>{plan.risk_score}/10</strong>
                  </Typography>
                )}
                {plan.ai_recommendations && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                    {Array.isArray(plan.ai_recommendations)
                      ? plan.ai_recommendations.join('; ')
                      : plan.ai_recommendations}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={handleViewInTMS}
                disabled={!plan.project_id || !plan.id}
              >
                View in TMS
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => onAskAgent(`Update test plan "${plan.name}" (ID: ${plan.id}) `)}
              >
                Ask to Update
              </Button>
            </Box>

            {/* Hint */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Ask the agent to update any field (e.g., "update priority to high")
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TestPlanDetailPanel;
